import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiStreamEvent, ApiStreamResult } from '@/backend/types';
import { invokeBackendRoute } from '@/backend/invoke';
import { useScoutChatContext } from './ScoutChatContext';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timeEntryPreview?: {
    timeEntries: Array<{
      description: string;
      matterId: string;
      userId: string;
      hours: number;
      rate: number;
      earnedYmd: { year: number; month: number; day: number };
      amount: number;
    }>;
  };
}

interface StreamingChunk {
  type: 'chunk' | 'complete' | 'error' | 'tool_call' | 'run_created';
  content: string;
}

interface ScoutChatRequestData {
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
  }>;
  conversationId?: string;
}

interface CancelOpenAIChatRequestData {
  threadId: string;
  runId: string;
}

interface ScoutConversation {
  id: string;
  title: string;
  lastMessagePreview: string;
  createdAt: string;
  updatedAt: string;
}

interface ListScoutConversationsResponse {
  conversations: ScoutConversation[];
}

interface GetScoutConversationMessagesResponse {
  conversation: ScoutConversation;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    createdAt: string;
  }>;
}

interface CreateScoutConversationResponse {
  conversation: ScoutConversation;
}

interface ChatResponseData {
  response: string;
}

interface DeleteScoutConversationResponse {
  status: 'success';
}

export interface UseScoutChatProps {
  opened: boolean;
  initialText?: string;
}

export interface UseScoutChatReturn {
  // State
  input: string;
  messages: Message[];
  showCloseConfirm: boolean;
  isLoading: boolean;
  isConversationLoading: boolean;
  conversations: ScoutConversation[];
  activeConversationId: string | null;

  // Refs (exposed for component use)
  scrollViewportRef: React.RefObject<HTMLDivElement>;
  textareaRef: React.RefObject<HTMLTextAreaElement>;

  // Computed
  canSend: boolean;

  // Handlers
  setInput: (value: string) => void;
  handleSend: () => Promise<void>;
  handleCancel: () => Promise<void>;
  handleCloseAttempt: () => void;
  handleConfirmClose: () => void;
  handleCancelClose: () => void;
  handleSelectConversation: (conversationId: string) => Promise<void>;
  handleStartNewConversation: () => Promise<void>;
  handleDeleteConversation: (conversationId: string) => Promise<void>;
  handlePrepareFreshChat: () => void;
}

export const useScoutChat = ({
  opened,
  initialText,
}: UseScoutChatProps): UseScoutChatReturn => {
  const context = useScoutChatContext();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>(context.messages);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [conversations, setConversations] = useState<ScoutConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);

  // Sync messages with context whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      context.setMessages(messages);
      context.setHasActiveChat(true);
    } else if (messages.length === 0 && context.hasActiveChat) {
      // Only clear if we're intentionally clearing (not on initial load)
      context.setHasActiveChat(false);
    }
  }, [messages]);

  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const autoSendRef = useRef<string | null>(null);
  const isAutoSendingRef = useRef<boolean>(false);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  const runIdRef = useRef<string | null>(null);
  const threadIdRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const hasLoadedConversationsRef = useRef(false);

  const cancelActiveRun = async () => {
    if (!runIdRef.current || !threadIdRef.current) {
      return;
    }

    await invokeBackendRoute<unknown, CancelOpenAIChatRequestData>(
      'cancelOpenAIChat',
      {
        threadId: threadIdRef.current,
        runId: runIdRef.current,
      },
    );
  };

  const loadConversationMessages = useCallback(
    async (conversationId: string) => {
      setIsConversationLoading(true);
      try {
        const response = await invokeBackendRoute<
          GetScoutConversationMessagesResponse,
          { conversationId: string }
        >('getScoutConversationMessages', { conversationId });
        const loadedMessages: Message[] = response.messages.map((message) => ({
          id: message.id,
          role: message.role,
          content: message.content,
        }));
        setMessages(loadedMessages);
        context.setMessages(loadedMessages);
        context.setHasActiveChat(loadedMessages.length > 0);
        setActiveConversationId(conversationId);
        conversationIdRef.current = conversationId;
      } finally {
        setIsConversationLoading(false);
      }
    },
    [context],
  );

  const loadConversations = useCallback(async () => {
    const response = await invokeBackendRoute<
      ListScoutConversationsResponse,
      { limit?: number }
    >('listScoutConversations', { limit: 30 });
    setConversations(response.conversations);
    return response.conversations;
  }, []);

  const handleSelectConversation = useCallback(
    async (conversationId: string) => {
      setInput('');
      await loadConversationMessages(conversationId);
    },
    [loadConversationMessages],
  );

  const handleStartNewConversation = useCallback(async () => {
    // Give immediate visual feedback that we switched away from the current chat.
    setMessages([]);
    context.setMessages([]);
    context.setHasActiveChat(false);
    setInput('');
    setActiveConversationId(null);
    conversationIdRef.current = null;
    setIsConversationLoading(true);

    try {
      const response = await invokeBackendRoute<
        CreateScoutConversationResponse,
        void
      >('createScoutConversation');
      const createdConversation = response.conversation;
      setConversations((prev) => [createdConversation, ...prev]);
      setActiveConversationId(createdConversation.id);
      conversationIdRef.current = createdConversation.id;
    } catch (error) {
      console.error('Failed to create Scout conversation', error);
      await loadConversations();
    } finally {
      setIsConversationLoading(false);
    }
  }, [context, loadConversations]);

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      setIsConversationLoading(true);
      try {
        await invokeBackendRoute<
          DeleteScoutConversationResponse,
          { conversationId: string }
        >('deleteScoutConversation', { conversationId });
        const updatedConversations = await loadConversations();
        if (updatedConversations.length > 0) {
          const nextConversation = updatedConversations[0];
          await loadConversationMessages(nextConversation.id);
          return;
        }
        setMessages([]);
        context.setMessages([]);
        context.setHasActiveChat(false);
        setActiveConversationId(null);
        conversationIdRef.current = null;
      } finally {
        setIsConversationLoading(false);
      }
    },
    [context, loadConversationMessages, loadConversations],
  );

  const handlePrepareFreshChat = useCallback(() => {
    setMessages([]);
    context.setMessages([]);
    context.setHasActiveChat(false);
    setActiveConversationId(null);
    conversationIdRef.current = null;
  }, [context]);

  useEffect(() => {
    if (!opened || hasLoadedConversationsRef.current) {
      return;
    }

    hasLoadedConversationsRef.current = true;
    loadConversations()
      .then((loadedConversations) => {
        if (loadedConversations.length === 0) {
          return;
        }
        const firstConversation = loadedConversations[0];
        return loadConversationMessages(firstConversation.id);
      })
      .catch((error) => {
        console.error('Failed to load Scout conversations', error);
      });
  }, [opened, loadConversationMessages, loadConversations]);

  useEffect(() => {
    if (opened) {
      setInput(initialText?.trim() ?? '');
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const length = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(length, length);
        }
        const emptyStateElement =
          document.querySelector('[data-message-id]') ||
          document.querySelector('h5');
        if (emptyStateElement) {
          emptyStateElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    }
  }, [opened, initialText]);

  // Track if we should auto-scroll (only when actively loading or when user is at bottom)
  const shouldAutoScrollRef = useRef(true);
  const lastMessageCountRef = useRef(0);
  const hasInitializedScrollRef = useRef(false);

  useEffect(() => {
    if (!scrollViewportRef.current) return;

    const viewport = scrollViewportRef.current;
    const isNewMessage = messages.length > lastMessageCountRef.current;
    lastMessageCountRef.current = messages.length;

    // On initial render with messages, scroll to bottom to show latest messages
    if (!hasInitializedScrollRef.current && messages.length > 0) {
      hasInitializedScrollRef.current = true;
      // Set scroll to bottom immediately (no animation) to show latest messages
      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior: 'auto',
      });
      shouldAutoScrollRef.current = true; // User is at bottom, so enable auto-scroll
      return;
    }

    // Auto-scroll when:
    // 1. We're loading (always scroll to follow the stream, even during content updates)
    // 2. It's a new message and user is near the bottom (within 100px)
    // 3. It's a new user message (always show it)
    const shouldScroll =
      isLoading ||
      (isNewMessage &&
        (messages[messages.length - 1]?.role === 'user' ||
          shouldAutoScrollRef.current));

    if (shouldScroll) {
      // Always scroll to the very bottom to show latest content
      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior: isLoading ? 'auto' : 'smooth', // Instant scroll during streaming for better UX
      });
      // Update shouldAutoScroll since we just scrolled to bottom
      shouldAutoScrollRef.current = true;
    }

    // Update shouldAutoScroll based on scroll position
    const handleScroll = () => {
      const isNearBottom =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <
        100;
      shouldAutoScrollRef.current = isNearBottom;
    };

    viewport.addEventListener('scroll', handleScroll);
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [messages, isLoading]);

  const canSend = useMemo(() => input.trim().length > 0, [input]);

  useEffect(() => {
    if (!opened) {
      setIsLoading(false);
      isProcessingRef.current = false;
      runIdRef.current = null;
      threadIdRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      isProcessingRef.current = false;
      runIdRef.current = null;
      threadIdRef.current = null;
    };
  }, [opened]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isProcessingRef.current = false;

      if (runIdRef.current && threadIdRef.current) {
        cancelActiveRun().catch(() => {
          // Ignore errors on unload
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleCancel = async () => {
    console.log('Canceling request...', {
      hasRunId: !!runIdRef.current,
      hasThreadId: !!threadIdRef.current,
      isProcessing: isProcessingRef.current,
    });

    isProcessingRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (runIdRef.current && threadIdRef.current) {
      try {
        await cancelActiveRun();
        console.log('OpenAI run cancelled successfully');
      } catch (error) {
        console.error('Failed to cancel OpenAI run:', error);
      }
      runIdRef.current = null;
      threadIdRef.current = null;
    }

    setIsLoading(false);

    setMessages((prev) =>
      prev.filter(
        (msg) =>
          msg.content !== 'Thinking...' &&
          msg.content !== 'Looking that up...' &&
          msg.content !== 'Creating...' &&
          !msg.content.startsWith('Checking ') &&
          !msg.content.startsWith('Creating '),
      ),
    );
  };

  const handleSend = async () => {
    if (isAutoSendingRef.current) {
      console.log('Prevented auto-send loop');
      return;
    }

    let text = input.trim();

    if (autoSendRef.current && text === autoSendRef.current) {
      const messageToAutoSend = autoSendRef.current;
      autoSendRef.current = null;
      isAutoSendingRef.current = true;
      text = messageToAutoSend;
    } else {
      if (!text) return;

      if (isLoading || isProcessingRef.current) {
        console.log('Run in progress - canceling before sending new message');
        await handleCancel();
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      const lowerText = text.toLowerCase().trim();
      const isCancelResponse =
        lowerText === 'yes' || lowerText === 'cancel' || lowerText === 'y';

      if (pendingMessage && isCancelResponse) {
        if (runIdRef.current && threadIdRef.current) {
          try {
            await cancelActiveRun();
            console.log('Active run cancelled, retrying message');
          } catch (error) {
            console.error('Failed to cancel active run:', error);
          }
        }

        const messageToRetry = pendingMessage;
        setPendingMessage(null);
        setInput('');

        setMessages((prev) => {
          const filtered = prev.filter(
            (msg) =>
              !msg.content.includes('still working on a previous request'),
          );
          const userResponse: Message = {
            id: crypto.randomUUID(),
            role: 'user',
            content: text,
          };
          return [...filtered, userResponse];
        });

        setTimeout(() => {
          setPendingMessage(null);
          setInput(messageToRetry);
          autoSendRef.current = messageToRetry;
          const autoSendTimeout = setTimeout(() => {
            if (
              autoSendRef.current === messageToRetry &&
              !isLoading &&
              !isProcessingRef.current &&
              !isAutoSendingRef.current
            ) {
              autoSendRef.current = null;
              isAutoSendingRef.current = true;
              handleSend();
              setTimeout(() => {
                isAutoSendingRef.current = false;
              }, 1000);
            }
          }, 200);

          timeoutRef.current = autoSendTimeout as any;
        }, 500);
        return;
      }
    }

    isProcessingRef.current = true;
    setIsLoading(true);

    abortControllerRef.current = new AbortController();

    let conversationIdForRequest = conversationIdRef.current;
    if (!conversationIdForRequest) {
      try {
        const created = await invokeBackendRoute<
          CreateScoutConversationResponse,
          void
        >('createScoutConversation');
        conversationIdForRequest = created.conversation.id;
        conversationIdRef.current = conversationIdForRequest;
        setActiveConversationId(conversationIdForRequest);
        setConversations((prev) => [created.conversation, ...prev]);
      } catch (error) {
        console.error('Failed to create Scout conversation', error);
      }
    }

    const newUser: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
    };
    setMessages((prev) => [...prev, newUser]);
    setInput('');

    const signal = abortControllerRef.current.signal;

    timeoutRef.current = setTimeout(() => {
      console.warn('Request timeout - canceling');
      handleCancel();
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content:
          'Sorry, that took too long. Please try again or refresh the page.',
      };
      setMessages((prev) => {
        const filtered = prev.filter(
          (msg) =>
            msg.content !== 'Thinking...' &&
            msg.content !== 'Looking that up...' &&
            msg.content !== 'Creating...' &&
            !msg.content.startsWith('Checking ') &&
            !msg.content.startsWith('Creating '),
        );
        return [...filtered, errorMessage];
      });
    }, 180000);

    const thinkingMessage: Message = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: 'Thinking...',
    };
    setMessages((prev) => [...prev, thinkingMessage]);

    try {
      const conversationHistory = [...messages, newUser].map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      let result: any;
      let streamRunStartedOrToolObserved = false;

      try {
        let timeoutId: NodeJS.Timeout | null = null;
        const createTimeoutPromise = () => {
          return new Promise((_, reject) => {
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(
              () => reject(new Error('Request timeout - please try again')),
              120000,
            );
          });
        };
        let timeoutPromise = createTimeoutPromise();

        let currentStreamingText = '';
        let streamRunStarted = false;
        let streamSawToolCall = false;
        console.log(
          'Attempting v9+ streaming with messages:',
          conversationHistory,
        );

        try {
          // S5-WB8 (PRA-768) flipped this route from `callableStream` to
          // `api-stream`. The api-stream invoker returns `{ stream }` only —
          // there is no terminal `data` promise like Firebase's
          // `HttpsCallableStreamResult` carries, because SSE has no
          // equivalent of a callable's "function return value". We
          // synthesize `{ data: { status, response, error } }` from the
          // `complete` / `error` chunks so the post-stream success/error
          // branching below stays untouched.
          const { stream } = await invokeBackendRoute<
            ApiStreamResult<ApiStreamEvent<StreamingChunk>>,
            ScoutChatRequestData
          >('openaiChatStreaming', {
            messages: conversationHistory,
            conversationId: conversationIdForRequest ?? undefined,
          });
          console.log('Got SSE stream');

          const processStream = async () => {
            try {
              let synthesizedResult: {
                data: {
                  status: 'success' | 'error';
                  response?: string;
                  error?: string;
                };
              } = {
                data: { status: 'success', response: '' },
              };

              for await (const sseEvent of stream) {
                if (!isProcessingRef.current) {
                  console.log('Processing cancelled, stopping stream');
                  break;
                }

                if (abortControllerRef.current?.signal.aborted) {
                  console.log('Stream aborted, stopping processing');
                  break;
                }
                // Each SSE event carries the legacy `StreamingChunk`
                // envelope on its `data` field — see the plugin in
                // services/api-longrun-node/src/routes/plugins/openaiChatStreaming.ts.
                const typedChunk = sseEvent.data;

                console.log('Received chunk:', typedChunk);

                timeoutPromise = createTimeoutPromise();

                if (typedChunk.type === 'chunk') {
                  currentStreamingText += typedChunk.content;
                  if (currentStreamingText.trim() !== '') {
                    streamRunStartedOrToolObserved = true;
                  }
                  console.log('Updated streaming text:', currentStreamingText);

                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === thinkingMessage.id
                        ? { ...msg, content: currentStreamingText }
                        : msg,
                    ),
                  );

                  // Removed artificial 10ms delay - React batches state updates automatically
                } else if (typedChunk.type === 'tool_call') {
                  streamSawToolCall = true;
                  streamRunStartedOrToolObserved = true;
                  // No second `createTimeoutPromise()` reset here — the
                  // unconditional reset above (every event resets the timer)
                  // already covers tool_call. The duplicate call existed in
                  // the legacy callable-stream consumer; Cursor Bugbot
                  // flagged it on the SSE migration as redundant.
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === thinkingMessage.id
                        ? { ...msg, content: typedChunk.content }
                        : msg,
                    ),
                  );
                } else if (typedChunk.type === 'run_created') {
                  try {
                    const data = JSON.parse(typedChunk.content);
                    runIdRef.current = data.runId;
                    threadIdRef.current = data.threadId;
                    if (
                      typeof data.conversationId === 'string' &&
                      data.conversationId.length > 0
                    ) {
                      conversationIdRef.current = data.conversationId;
                      setActiveConversationId(data.conversationId);
                    }
                    streamRunStarted = true;
                    streamRunStartedOrToolObserved = true;
                    console.log('Run created, tracking for cancellation', {
                      runId: data.runId,
                      threadId: data.threadId,
                    });
                  } catch (e) {
                    console.error('Failed to parse run_created data:', e);
                  }
                } else if (typedChunk.type === 'complete') {
                  console.log('Stream complete');
                  const finalResponse =
                    typedChunk.content?.trim() || currentStreamingText;
                  if (finalResponse) {
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === thinkingMessage.id
                          ? { ...msg, content: finalResponse }
                          : msg,
                      ),
                    );
                  }
                  synthesizedResult = {
                    data: { status: 'success', response: finalResponse },
                  };
                  setIsLoading(false);
                  isProcessingRef.current = false;
                  break;
                } else if (typedChunk.type === 'error') {
                  console.error('Stream error received:', typedChunk.content);
                  // Throw to flow into the outer streaming-failed catch,
                  // which retries via the non-streaming `openaiChat`
                  // route. Same fallback path as the legacy
                  // `callableStream` consumer.
                  throw new Error(typedChunk.content || 'Stream error');
                }
              }

              return synthesizedResult;
            } catch (streamError) {
              console.error('Stream processing error:', streamError);
              throw streamError;
            }
          };

          result = (await Promise.race([
            processStream(),
            timeoutPromise,
          ])) as any;

          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        } catch (streamingError) {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          const shouldSkipNonStreamingRetry =
            streamRunStarted ||
            streamSawToolCall ||
            currentStreamingText.trim() !== '';
          if (shouldSkipNonStreamingRetry) {
            streamRunStartedOrToolObserved = true;
            throw streamingError;
          }
          console.log(
            'Server-side streaming failed, using client-side simulation:',
            streamingError,
          );

          // The non-streaming route now throws `ApiHttpError` on failure
          // (HTTP 4xx/5xx + canonical envelope) and returns `{ response }`
          // directly on success — no more `{ status, response?, error? }`
          // wrapper. The Promise.race's error path therefore needs no
          // explicit envelope branching; an HTTP error flows straight to
          // the outer catch.
          const regularData = (await Promise.race([
            invokeBackendRoute<ChatResponseData, ScoutChatRequestData>(
              'openaiChatNonStreaming',
              {
                messages: conversationHistory,
                conversationId: conversationIdForRequest ?? undefined,
              },
            ),
            timeoutPromise,
          ])) as ChatResponseData;

          const fullResponse = regularData.response || 'No response received';

          // Show the full reply at once. Word-by-word animation added ~20 ms
          // per token and made Scout feel unusably slow on long answers
          // (especially before a demo).
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === thinkingMessage.id
                ? { ...msg, content: fullResponse }
                : msg,
            ),
          );

          setIsLoading(false);
          isProcessingRef.current = false;

          result = { data: { status: 'success', response: fullResponse } };
        }

        // `result.data.status` is the streaming-path's internal envelope (the
        // SSE chunks carry their own `complete` / `error` markers, captured
        // via `synthesizedResult` above). The non-streaming fallback above
        // also folds into this shape so the success-message rendering stays
        // in one branch. Post-PRA-814 the HTTP error path throws before
        // reaching here, so the `'error'` branch is unreachable.
        if (result && result.data && result.data.status === 'success') {
          const finalResponse = result.data.response || currentStreamingText;

          let timeEntryPreview: Message['timeEntryPreview'] | undefined;
          try {
            const jsonMatch = finalResponse.match(
              /\{[\s\S]*"preview"[\s\S]*"timeEntries"[\s\S]*\}/,
            );
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              if (
                parsed.preview === true &&
                Array.isArray(parsed.timeEntries)
              ) {
                timeEntryPreview = { timeEntries: parsed.timeEntries };
              }
            }
          } catch (e) {
            // Not a preview, continue normally
          }

          if (currentStreamingText && currentStreamingText !== finalResponse) {
            const finalMessage: Message = {
              id: thinkingMessage.id,
              role: 'assistant',
              content: finalResponse,
              timeEntryPreview,
            };
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === thinkingMessage.id ? finalMessage : msg,
              ),
            );
          } else if (timeEntryPreview) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === thinkingMessage.id
                  ? { ...msg, timeEntryPreview }
                  : msg,
              ),
            );
          }

          setIsLoading(false);
          isProcessingRef.current = false;
        }
      } catch (streamingError) {
        if (streamRunStartedOrToolObserved) {
          throw streamingError;
        }
        console.log(
          'Streaming failed, falling back to v8 compat:',
          streamingError,
        );

        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error('Request timeout - please try again')),
            120000,
          );
        });

        // Outer fallback: the non-streaming `openaiChat` route also throws
        // on HTTP error post-migration, so we no longer need the explicit
        // `{ status, error }` envelope check below.
        const finalData = (await Promise.race([
          invokeBackendRoute<ChatResponseData, ScoutChatRequestData>(
            'openaiChat',
            {
              messages: conversationHistory,
              conversationId: conversationIdForRequest ?? undefined,
            },
          ),
          timeoutPromise,
        ])) as ChatResponseData;

        const finalMessage: Message = {
          id: thinkingMessage.id,
          role: 'assistant',
          content: finalData.response || 'No response received',
        };
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === thinkingMessage.id ? finalMessage : msg,
          ),
        );
      }
    } catch (error) {
      if (abortControllerRef.current?.signal.aborted) {
        console.log('Request was aborted');
      } else {
        console.error('Error sending message to Scout:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        const isActiveRunError =
          errorMessage.includes('while a run') &&
          errorMessage.includes('is active');

        if (isActiveRunError) {
          const runIdMatch = errorMessage.match(/run_([a-zA-Z0-9]+)/);
          if (runIdMatch && runIdMatch[1]) {
            runIdRef.current = runIdMatch[1];
          }

          const threadIdMatch = errorMessage.match(/thread_([a-zA-Z0-9]+)/);
          if (threadIdMatch && threadIdMatch[1]) {
            threadIdRef.current = threadIdMatch[1];
          }

          setPendingMessage(text);

          setIsLoading(false);
          isProcessingRef.current = false;

          const cancelPromptMessage: Message = {
            id: thinkingMessage.id,
            role: 'assistant',
            content:
              "I'm still working on a previous request. Would you like me to cancel it and start fresh?",
          };
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === thinkingMessage.id ? cancelPromptMessage : msg,
            ),
          );
        } else {
          const errorMessageObj: Message = {
            id: thinkingMessage.id,
            role: 'assistant',
            content: `Sorry, I encountered an error: ${errorMessage}`,
          };
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === thinkingMessage.id ? errorMessageObj : msg,
            ),
          );
        }
      }
    } finally {
      loadConversations().catch((conversationError) => {
        console.error(
          'Failed to refresh Scout conversations',
          conversationError,
        );
      });
      if (isLoading) {
        setIsLoading(false);
      }
      isProcessingRef.current = false;
      isAutoSendingRef.current = false;
      runIdRef.current = null;
      threadIdRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current = null;
      }
    }
  };

  const handleCloseAttempt = () => {
    if (input.trim().length > 0) {
      setShowCloseConfirm(true);
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    setInput('');
  };

  const handleCancelClose = () => {
    setShowCloseConfirm(false);
  };

  return {
    input,
    messages,
    showCloseConfirm,
    isLoading,
    isConversationLoading,
    conversations,
    activeConversationId,
    scrollViewportRef,
    textareaRef,
    canSend,
    setInput,
    handleSend,
    handleCancel,
    handleCloseAttempt,
    handleConfirmClose,
    handleCancelClose,
    handleSelectConversation,
    handleStartNewConversation,
    handleDeleteConversation,
    handlePrepareFreshChat,
  };
};
